/**
 * The one place the client talks to the server.
 *
 * Three things it handles so no screen has to:
 *
 *  - the token, attached to every request and refreshed once when it expires
 *  - the envelope, unwrapped so callers get `data` rather than `{success,data}`
 *  - failure, turned into an Error with a message written for a cashier
 *
 * On a POS the last one matters more than usual. The person reading the toast
 * is standing at a counter with a queue behind them, so "Could not reach the
 * till database" is useful and "Request failed with status code 500" is not.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_PREFIX = `${BASE_URL}/api`;

/**
 * Turn a stored image path into something an <img> can load.
 *
 * The server stores and returns `/uploads/menu/abc.png` - relative, because on
 * a normal single-machine install the API serves the UI from its own origin and
 * relative is exactly right. A second till pointing at the counter PC is the
 * case that breaks: there the browser would ask its own origin for a file that
 * only exists on the other machine. Prefixing with the same base the API calls
 * use keeps both installs working off one stored value.
 *
 * Absolute URLs and blob/data URIs pass through untouched.
 */
export const assetUrl = (path) => {
    if (!path) return path;
    if (/^(https?:|data:|blob:|file:)/i.test(path)) return path;
    return `${BASE_URL}${path}`;
};

const TOKEN_KEY = 'pos.token';
const REFRESH_KEY = 'pos.refreshToken';
const SESSION_KEY = 'pos.userSessionId';

/* --------------------------- token storage -------------------------------- */

export const tokenStore = {
    get: () => localStorage.getItem(TOKEN_KEY),
    getRefresh: () => localStorage.getItem(REFRESH_KEY),
    getSessionId: () => localStorage.getItem(SESSION_KEY),
    set: ({ token, refreshToken, userSessionId }) => {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
        if (userSessionId) localStorage.setItem(SESSION_KEY, String(userSessionId));
    },
    clear: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(SESSION_KEY);
    }
};

/**
 * The terminal name travels on every request so the server can tie orders and
 * shifts to a physical till. Generated once and kept, because "which machine
 * was this rung up on" is a question that gets asked at closing time.
 */
const getTerminalName = () => {
    let name = localStorage.getItem('pos.terminal');
    if (!name) {
        name = `TILL-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
        localStorage.setItem('pos.terminal', name);
    }
    return name;
};

/* ------------------------------ error type -------------------------------- */

export class ApiError extends Error {
    constructor(message, { status, code, details } = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

/**
 * Called when a request comes back 401 and cannot be refreshed. The app sets
 * this at startup so the api layer can sign the user out without importing the
 * store and creating a cycle.
 */
let onUnauthorised = () => {};
export const setUnauthorisedHandler = (handler) => { onUnauthorised = handler; };

/* ------------------------------- the fetch -------------------------------- */

let refreshInFlight = null;

const refreshToken = async () => {
    // Several requests can fail at once when a token expires; they should share
    // one refresh rather than each starting their own.
    if (refreshInFlight) return refreshInFlight;

    const refresh = tokenStore.getRefresh();
    if (!refresh) return null;

    refreshInFlight = (async () => {
        try {
            const response = await fetch(`${API_PREFIX}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: refresh, terminalName: getTerminalName() })
            });

            if (!response.ok) return null;

            const body = await response.json();
            if (!body?.data?.token) return null;

            tokenStore.set(body.data);
            return body.data.token;
        } catch {
            return null;
        } finally {
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
};

const request = async (method, path, { body, params, signal, retryOn401 = true } = {}) => {
    const url = new URL(`${API_PREFIX}${path}`, window.location.origin);

    if (params) {
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null || value === '') continue;
            url.searchParams.set(key, value instanceof Date ? value.toISOString() : String(value));
        }
    }

    const headers = {
        Accept: 'application/json',
        'X-Terminal-Name': getTerminalName()
    };

    const token = tokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;

    /**
     * FormData carries its own content type, including a multipart boundary the
     * browser generates. Setting Content-Type by hand here would send the type
     * without that boundary, and the server would find no fields at all - so a
     * file upload goes out untouched, and everything else is JSON.
     */
    const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && !isMultipart) headers['Content-Type'] = 'application/json';

    let response;
    try {
        response = await fetch(url.toString(), {
            method,
            headers,
            body: body === undefined ? undefined : (isMultipart ? body : JSON.stringify(body)),
            signal
        });
    } catch (err) {
        if (err.name === 'AbortError') throw err;
        // The server process is down, not the internet - the app and the API
        // are on the same machine.
        throw new ApiError(
            'Cannot reach the till service. Check that the POS server is running.',
            { code: 'NETWORK' }
        );
    }

    // 204 has no body to parse.
    if (response.status === 204) return null;

    let payload = null;
    try {
        payload = await response.json();
    } catch {
        if (response.ok) return null;
        throw new ApiError(`The server returned an unexpected response (${response.status}).`, {
            status: response.status
        });
    }

    if (response.status === 401 && retryOn401 && payload?.code === 'TOKEN_EXPIRED') {
        const fresh = await refreshToken();
        if (fresh) {
            // One retry only; a second 401 means the refresh token has gone too.
            return request(method, path, { body, params, signal, retryOn401: false });
        }
        tokenStore.clear();
        onUnauthorised();
        throw new ApiError(payload.message || 'Your session has expired. Sign in again.', {
            status: 401, code: payload.code
        });
    }

    if (response.status === 401) {
        tokenStore.clear();
        onUnauthorised();
    }

    if (!response.ok || payload?.success === false) {
        throw new ApiError(payload?.message || `Request failed (${response.status}).`, {
            status: response.status,
            code: payload?.code,
            details: payload?.details
        });
    }

    // `meta` carries paging; return the whole envelope when it is present so a
    // table can render "showing 21-40 of 137".
    return payload.meta ? { data: payload.data, meta: payload.meta } : payload.data;
};

export const api = {
    get: (path, params, options) => request('GET', path, { params, ...options }),
    post: (path, body, options) => request('POST', path, { body, ...options }),
    put: (path, body, options) => request('PUT', path, { body, ...options }),
    patch: (path, body, options) => request('PATCH', path, { body, ...options }),
    delete: (path, options) => request('DELETE', path, options),

    /**
     * Send one file as multipart.
     *
     * Separate from `post` only so callers read as what they are doing; it goes
     * through the same request path, so it gets the same token handling, the
     * same 401 refresh and the same error messages.
     */
    upload: (path, file, { field = 'image', ...options } = {}) => {
        const form = new FormData();
        form.append(field, file, file.name);
        return request('POST', path, { body: form, ...options });
    },

    terminalName: getTerminalName
};

export default api;
